/** Detalle de tarea con resumen del documento y decisión (shape real del backend). */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileTextarea } from '@/components/MobileInput';
import { MobileIcon } from '@/components/MobileIcon';
import { SectionCard } from '@/components/SectionCard';
import { StatusBadge } from '@/components/StatusBadge';
import { DetailSkeleton } from '@/components/LoadingSkeleton';
import { hapticSuccess, hapticWarning } from '@/utils/haptic';
import {
  useDecideTaskMutation,
  useMobileTaskContextQuery,
  useMobileTaskQuery,
} from '@/hooks/useMobileTasks';
import { useMobileDocumentQuery, useQuotationPdfMutation } from '@/hooks/useMobileQuotations';
import { decisionError, taskDecisionSchema, type TaskDecisionForm } from '@/schemas/task-decision.schemas';
import { extractErrorMessage, extractRevisionConflict } from '@/services/mobileApiClient';
import { formatCurrency, formatRelativeDate } from '@/utils/format';
import type {
  MobileBudgetDto,
  MobileQuotationDto,
  MobileRequirementDto,
} from '@/services/commercialService';

const ENTITY_LABELS: Record<string, string> = {
  budget: 'Presupuesto',
  quotation: 'Cotización',
  customer_requirement: 'Requerimiento',
};

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function toDisplayNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function TaskDetailPage() {
  const { taskId = '' } = useParams();
  const navigate = useNavigate();
  const detailQuery = useMobileTaskQuery(taskId);
  const detail = detailQuery.data;
  const task = detail?.task;
  const instanceSummary = detail?.instance_summary;
  const documentSummary = detail?.document_summary;
  const entityCode = instanceSummary?.entity_code;
  const entityId = instanceSummary?.entity_id;
  const contextQuery = useMobileTaskContextQuery(entityCode, entityId);
  const documentQuery = useMobileDocumentQuery(entityCode, entityId);
  const decideMutation = useDecideTaskMutation();
  const pdfMutation = useQuotationPdfMutation();
  const [pendingDecision, setPendingDecision] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const {
    register,
    getValues,
    formState: { errors },
  } = useForm<TaskDecisionForm>({
    resolver: yupResolver(taskDecisionSchema),
    defaultValues: { decision: 'approve', reason: '' },
  });

  function ask(decision: 'approve' | 'reject') {
    if (decision === 'reject' && getValues().reason.trim() === '') {
      setError('El rechazo exige un motivo');
      return;
    }
    setError(null);
    setPendingDecision(decision);
  }

  async function confirm() {
    if (!detail || !task || !pendingDecision) return;
    const values = getValues();
    const ruleError = decisionError({ decision: pendingDecision, reason: values.reason });
    if (ruleError) {
      setError(ruleError);
      setPendingDecision(null);
      return;
    }
    setError(null);
    try {
      if (pendingDecision === 'approve') hapticSuccess();
      else hapticWarning();

      await decideMutation.mutateAsync({
        taskId,
        decision: pendingDecision,
        reason: values.reason.trim() || undefined,
        expectedTaskRevision: detail.task_revision,
      });
      navigate('/m/tasks', { replace: true });
    } catch (err) {
      if (extractRevisionConflict(err)) {
        setStale(true);
        setError('El documento cambió. Vuelve a la bandeja y reabre la tarea.');
      } else {
        setError(extractErrorMessage(err));
      }
      setPendingDecision(null);
    }
  }

  const document = documentQuery.data;

  if (detailQuery.isPending) {
    return (
      <MobileLayout title="Tarea" showBack>
        <DetailSkeleton />
      </MobileLayout>
    );
  }

  if (!detail || !task || !instanceSummary || !documentSummary) {
    return (
      <MobileLayout title="Tarea" showBack>
        <p className="p-8 text-center text-text-secondary">Tarea no encontrada.</p>
      </MobileLayout>
    );
  }

  const entityLabel = ENTITY_LABELS[instanceSummary.entity_code] ?? instanceSummary.entity_code.replace(/_/g, ' ');

  return (
    <MobileLayout
      title={entityLabel}
      subtitle={instanceSummary.workflow_point_code}
      showBack
    >
      <div className="space-y-4">
        <SectionCard
          title={entityLabel}
          action={<StatusBadge status={document?.status ?? documentSummary.business_status} size="sm" />}
        >
          <p className="break-words text-lg font-bold text-text-primary">
            {documentSummary.label}
          </p>

          {document && (
            <p title={document.id} className="truncate text-sm text-text-secondary">
              {entityCode === 'customer_requirement'
                ? (document as MobileRequirementDto).code ||
                  (document as MobileRequirementDto).title ||
                  document.id
                : entityCode === 'quotation'
                  ? (document as MobileQuotationDto).quotation_number ||
                    (document as MobileQuotationDto).code ||
                    document.id
                  : entityCode === 'budget'
                    ? (document as MobileBudgetDto).budget_code ||
                      (document as MobileBudgetDto).code ||
                      document.id
                    : document.id}
            </p>
          )}

          {document && entityCode === 'customer_requirement' && (
            <dl className="mt-2 divide-y divide-border-light text-sm">
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Código</dt>
                <dd title={(document as MobileRequirementDto).code} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                  {(document as MobileRequirementDto).code || '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cliente</dt>
                <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                  {(document as MobileRequirementDto).client?.legal_name || '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Fecha requerida</dt>
                <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                  {formatDate((document as MobileRequirementDto).required_date)}
                </dd>
              </div>
            </dl>
          )}
          {document && entityCode === 'customer_requirement' &&
            (document as MobileRequirementDto).description && (
              <p className="mt-2 break-words border-t border-border-light pt-3 text-sm text-text-secondary">
                {(document as MobileRequirementDto).description}
              </p>
            )}

          {document && entityCode === 'quotation' && (
            <dl className="mt-2 divide-y divide-border-light text-sm">
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Número</dt>
                <dd title={(document as MobileQuotationDto).quotation_number} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                  {(document as MobileQuotationDto).quotation_number ||
                    (document as MobileQuotationDto).code ||
                    '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cliente</dt>
                <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                  {(document as MobileQuotationDto).project?.client_legal_name ||
                    (document as MobileQuotationDto).project?.name ||
                    '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Total</dt>
                <dd className="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-text-primary">
                  {formatCurrency(
                    toDisplayNumber((document as MobileQuotationDto).total),
                    (document as MobileQuotationDto).currency ?? 'PEN',
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Vigencia</dt>
                <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                  {formatDate((document as MobileQuotationDto).valid_until)}
                </dd>
              </div>
            </dl>
          )}

          {document && entityCode === 'budget' && (
            <dl className="mt-2 divide-y divide-border-light text-sm">
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Código</dt>
                <dd title={(document as MobileBudgetDto).budget_code} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                  {(document as MobileBudgetDto).budget_code ||
                    (document as MobileBudgetDto).code ||
                    '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cliente</dt>
                <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                  {(document as MobileBudgetDto).customer_requirement?.client?.legal_name || '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Total</dt>
                <dd className="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-text-primary">
                  {formatCurrency(
                    toDisplayNumber(
                      (document as MobileBudgetDto).total ??
                        (document as MobileBudgetDto).sale_price,
                    ),
                    (document as MobileBudgetDto).currency ?? 'USD',
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Estado</dt>
                <dd className="min-w-0 flex-1 truncate text-right text-text-secondary">
                  {document.status || '—'}
                </dd>
              </div>
            </dl>
          )}

          {entityCode === 'quotation' && entityId && (
            <MobileButton
              variant="secondary"
              size="sm"
              fullWidth
              className="mt-3"
              loading={pdfMutation.isPending}
              onClick={() => void pdfMutation.mutateAsync(entityId)}
              icon={<MobileIcon name="picture_as_pdf" className="h-5 w-5" />}
            >
              Descargar PDF
            </MobileButton>
          )}
        </SectionCard>

        {(contextQuery.data?.points ?? []).length > 0 && (
          <SectionCard title="Recorrido del workflow">
            <ol className="relative ml-3 space-y-3 border-l-2 border-border">
              {contextQuery.data!.points.map((point, idx) => (
                <li key={point.code} className="relative pl-4">
                  <span className="absolute -left-[11px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary bg-primary-light">
                  <MobileIcon name={idx === 0 ? 'flag' : 'circle'} className="h-3 w-3 text-primary" />
                    </span>
                  <p className="break-words text-sm font-medium text-text-primary line-clamp-2">{point.label}</p>
                </li>
              ))}
            </ol>
          </SectionCard>
        )}

        <SectionCard title="Estado de la tarea">
          <div className="flex items-center justify-between gap-2">
            <StatusBadge status={task.status} />
            {task.due_at ? (
              <div className="shrink-0 text-right">
                <p className="text-xs uppercase tracking-wider text-text-tertiary">Vence</p>
                <p className="truncate text-sm font-medium text-text-primary">
                  {formatRelativeDate(task.due_at)}
                </p>
              </div>
            ) : (
              <div className="shrink-0 text-right">
                <p className="text-xs uppercase tracking-wider text-text-tertiary">Plazo</p>
                <p className="truncate text-sm font-medium text-text-primary">Sin plazo</p>
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Decisión">
          <MobileTextarea
            id="reason"
            label="Motivo (obligatorio para rechazar)"
            rows={3}
            placeholder="Describe el motivo de tu decisión..."
            error={errors.reason?.message}
            aria-invalid={errors.reason ? true : undefined}
            {...register('reason')}
          />
        </SectionCard>

        {error && (
          <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{error}</p>
          </div>
        )}

        {stale && (
          <MobileButton
            variant="secondary"
            size="md"
            fullWidth
            onClick={() => navigate('/m/tasks', { replace: true })}
            icon={<MobileIcon name="refresh" className="h-5 w-5" />}
          >
            Volver a la bandeja y reabrir
          </MobileButton>
        )}

        <div className="grid grid-cols-1 gap-3">
          <MobileButton
            variant="success"
            size="lg"
            fullWidth
            loading={decideMutation.isPending}
            disabled={stale}
            onClick={() => ask('approve')}
            icon={<MobileIcon name="check_circle" className="h-5 w-5" />}
          >
            Aprobar
          </MobileButton>
          <MobileButton
            variant="danger"
            size="lg"
            fullWidth
            loading={decideMutation.isPending}
            disabled={stale}
            onClick={() => ask('reject')}
            icon={<MobileIcon name="cancel" className="h-5 w-5" />}
          >
            Rechazar
          </MobileButton>
        </div>
      </div>

      {pendingDecision && (
        <ConfirmDialog
          title={pendingDecision === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
          message={
            pendingDecision === 'approve'
              ? 'Se registrará tu aprobación con la revisión actual del documento.'
              : `Se registrará el rechazo${getValues().reason.trim() ? `: ${getValues().reason.trim()}` : ''}.`
          }
          confirmLabel={pendingDecision === 'approve' ? 'Sí, aprobar' : 'Sí, rechazar'}
          danger={pendingDecision === 'reject'}
          busy={decideMutation.isPending}
          onConfirm={() => void confirm()}
          onCancel={() => setPendingDecision(null)}
        />
      )}
    </MobileLayout>
  );
}
